import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import {
  fetchClients,
  fetchModules,
  getUniqueProductsFromClients,
  parseModuleIds,
  Client,
  Product,
  Module,
  submitNewEnquiry,
  fetchEnquiriesForTable,
  TableEnquiryRow,
  updateEnquiry,
  deleteEnquiry,
} from "../../core/api/useEnquiryStatus";
import { all_routes } from "../router/all_routes";
import Table from "../../core/common/dataTable/index";
import { Checkbox, Drawer, Input, Modal, Popover, Tag } from "antd";
import {
  exportAllEnquiriesExcel,
  exportAllEnquiriesPdf,
  exportSelectedEnquiriesExcel,
  exportSelectedEnquiriesPdf,
} from "../../core/api/useEnquiryExport";
import { Button, Dropdown, Menu, Select } from "antd";
import {
  DownOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ExportOutlined,
  PlusCircleOutlined,
  EyeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../core/data/redux/store";
import { fetchProducts } from "../../core/api/products";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuthToken } from "../../core/data/redux/useAuthToken";
import { usePermission } from "../../core/api/useRolePermission";
import {
  getAllCategories,
  getCategoryById,
  createCategory,
  updateCategory,
  CategoryPayload,
  CategoryResponse,
} from "../../core/api/category";

const statusColors = {
  "In Progress": { color: "#0d6efd", icon: "🛈" },
  "Pending To Assign": { color: "#fd7e14", icon: "⚠️" },
  Rejected: { color: "#dc3545", icon: "⛔" },
  "On Hold": { color: "#6f42c1", icon: "⏸️" },
  Completed: { color: "#198754", icon: "✅" },
  Pending: { color: "#ffc107", icon: "⌛" },
  "Yet to Start": { color: "#0d6efd", icon: "✔️" },
};

const Enquiry: React.FC = () => {
  const canCreate = usePermission("Enquiries", "create");
  const canUpdate = usePermission("Enquiries", "write");
  const canRead = usePermission("Enquiries", "read");
  const canDelete = usePermission("Enquiries", "delete");
  const [showNewEnquiry, setShowNewEnquiry] = useState(true);
  const [showEnquiryStatus, setShowEnquiryStatus] = useState(false);
  const [email, setEmail] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [companyType, setCompanyType] = useState("existing");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [selectedModule, setSelectedModule] = useState("");
  const [requirementType, setRequirementType] = useState("");
  const [requirements, setRequirements] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const EmpId = useSelector((state: any) => state.auth.employeeId);
  const empToken = useAuthToken();
  const [filteredModules, setFilteredModules] = useState<Module[]>([]);
  const [activeEnquiryTab, setActiveEnquiryTab] = useState<"new" | "status">(
    "new"
  );
  const [searchText, setSearchText] = useState("");
  const [licenseList, setLicenseList] = useState<string[]>([]);
  const [companyLicense, setCompanyLicense] = useState("");
  const [category, setCategory] = useState<number | null>(null); // Changed to number | null
  const routes = all_routes;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const capitalizeFirstLetter = (str: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  // Edit Modal States
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editEnquiry, setEditEnquiry] = useState<TableEnquiryRow | null>(null);
  const [editRequirementType, setEditRequirementType] = useState("");
  const [editRequirements, setEditRequirements] = useState("");
  const [editAttachments, setEditAttachments] = useState<File[]>([]);
  const editFileInputRef = useRef<HTMLInputElement | null>(null);
  const [editEnquiryCategory, setEditEnquiryCategory] = useState<number | null>(
    null
  ); // Changed to number | null

  // Delete Modal States
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteEnquiryId, setDeleteEnquiryId] = useState<string | null>(null);

  // Category Modal States
  const [addCategoryModalVisible, setAddCategoryModalVisible] = useState(false);
  const [editCategoryModalVisible, setEditCategoryModalVisible] =
    useState(false);
  const [viewCategoryModalVisible, setViewCategoryModalVisible] =
    useState(false);
  const [manageCategoryModalVisible, setManageCategoryModalVisible] =
    useState(false);
  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryResponse | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [dataSource, setDataSource] = useState<TableEnquiryRow[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<TableEnquiryRow | null>(
    null
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEnquiryIds, setSelectedEnquiryIds] = useState<string[]>([]);

  // Filter States
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    enquiryId: [] as string[],
    companyName: [] as string[],
    reqType: [] as string[],
    category: [] as string[],
  });
  const [modalFilters, setModalFilters] = useState({
    enquiryId: [] as string[],
    companyName: [] as string[],
    reqType: [] as string[],
    category: [] as string[],
  });

  const activeFiltersCount = useMemo(
    () =>
      Object.values(filters).filter((f) => Array.isArray(f) && f.length > 0)
        .length,
    [filters]
  );

  // Unique values for dropdown filters
  const uniqueEnquiryIds = useMemo(() => {
    if (!dataSource || !Array.isArray(dataSource)) return [];
    return Array.from(
      new Set(dataSource.map((row) => row.enquiry_id).filter((id) => id))
    ).sort();
  }, [dataSource]);

  const uniqueCompanyNames = useMemo(() => {
    if (!dataSource || !Array.isArray(dataSource)) return [];
    return Array.from(
      new Set(dataSource.map((row) => row.company_name).filter((name) => name))
    ).sort();
  }, [dataSource]);

  const uniqueReqTypes = useMemo(() => {
    if (!dataSource || !Array.isArray(dataSource)) return [];
    return Array.from(
      new Set(dataSource.map((row) => row.enquiry_type).filter((type) => type))
    ).sort();
  }, [dataSource]);

  const allEnquiryIds = useMemo(() => uniqueEnquiryIds, [uniqueEnquiryIds]);
  const allCompanyNames = useMemo(
    () => uniqueCompanyNames,
    [uniqueCompanyNames]
  );
  const allReqTypes = useMemo(() => uniqueReqTypes, [uniqueReqTypes]);
  const allCategories = useMemo(
    () => categories.map((cat) => cat.cate_name),
    [categories]
  );

  // Checked and indeterminate states
  const enquiryIdChecked = useMemo(
    () =>
      allEnquiryIds.length > 0 &&
      modalFilters.enquiryId.length === allEnquiryIds.length,
    [allEnquiryIds, modalFilters.enquiryId]
  );
  const enquiryIdIndeterminate = useMemo(
    () =>
      modalFilters.enquiryId.length > 0 &&
      modalFilters.enquiryId.length < allEnquiryIds.length,
    [allEnquiryIds, modalFilters.enquiryId]
  );

  const companyNameChecked = useMemo(
    () =>
      allCompanyNames.length > 0 &&
      modalFilters.companyName.length === allCompanyNames.length,
    [allCompanyNames, modalFilters.companyName]
  );
  const companyNameIndeterminate = useMemo(
    () =>
      modalFilters.companyName.length > 0 &&
      modalFilters.companyName.length < allCompanyNames.length,
    [allCompanyNames, modalFilters.companyName]
  );

  const reqTypeChecked = useMemo(
    () =>
      allReqTypes.length > 0 &&
      modalFilters.reqType.length === allReqTypes.length,
    [allReqTypes, modalFilters.reqType]
  );
  const reqTypeIndeterminate = useMemo(
    () =>
      modalFilters.reqType.length > 0 &&
      modalFilters.reqType.length < allReqTypes.length,
    [allReqTypes, modalFilters.reqType]
  );

  const categoryChecked = useMemo(
    () =>
      allCategories.length > 0 &&
      modalFilters.category.length === allCategories.length,
    [allCategories, modalFilters.category]
  );
  const categoryIndeterminate = useMemo(
    () =>
      modalFilters.category.length > 0 &&
      modalFilters.category.length < allCategories.length,
    [allCategories, modalFilters.category]
  );

  const showModal = () => {
    setModalFilters({ ...filters });
    setFilterModalVisible(true);
  };

  const handleFilterCancel = () => setFilterModalVisible(false);

  const handleFilterApply = () => {
    setFilters({ ...modalFilters });
    setFilterModalVisible(false);
  };

  const handleFilterClear = () => {
    setModalFilters({
      enquiryId: [],
      companyName: [],
      reqType: [],
      category: [],
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const filterModalRef = useRef<HTMLDivElement>(null);

  const dropdownRender = (
    menus: React.ReactNode,
    onSelectAll: (e: any) => void,
    checked: boolean,
    indeterminate: boolean,
    title: string
  ) => (
    <div>
      <div style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
        <Checkbox
          indeterminate={indeterminate}
          checked={checked}
          onChange={onSelectAll}
        >
          Select all {title}
        </Checkbox>
      </div>
      <div style={{ maxHeight: 200, overflow: "auto" }}>{menus}</div>
    </div>
  );

  const handleSelectAllEnquiryId = (e: any) => {
    setModalFilters((prev) => ({
      ...prev,
      enquiryId: e.target.checked ? allEnquiryIds : [],
    }));
  };

  const handleSelectAllCompanyName = (e: any) => {
    setModalFilters((prev) => ({
      ...prev,
      companyName: e.target.checked ? allCompanyNames : [],
    }));
  };

  const handleSelectAllReqType = (e: any) => {
    setModalFilters((prev) => ({
      ...prev,
      reqType: e.target.checked ? allReqTypes : [],
    }));
  };

  const handleSelectAllCategory = (e: any) => {
    setModalFilters((prev) => ({
      ...prev,
      category: e.target.checked ? allCategories : [],
    }));
  };

  const handleNewEnquiryClick = (tab: "new") => {
    if (!canCreate) {
      toast.warning("You don't have permission to Create");
      return;
    }
    setShowNewEnquiry(true);
    setActiveEnquiryTab(tab);
    setShowEnquiryStatus(false);
  };

  const handleEnquiryStatusClick = (tab: "status") => {
    if (!canRead) {
      toast.warning("You don't have permission to View");
      return;
    }
    setShowEnquiryStatus(true);
    setActiveEnquiryTab(tab);
    setShowNewEnquiry(false);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const clientData = await fetchClients(empToken);
        const moduleData = await fetchModules(empToken);
        const productData = await fetchProducts(empToken);
        const licenses = Array.from(
          new Set(clientData.map((c) => c.company_license))
        );
        setLicenseList(licenses);
        setClients(clientData);
        setModules(moduleData);
        setProducts(productData as Product[]);
        const categoryData = await getAllCategories(empToken);
        setCategories(categoryData);
      } catch (err) {
        console.error("Error loading enquiry data:", err);
      }
    };
    loadData();
  }, [empToken]);

  useEffect(() => {
    if (companyType === "existing" && selectedCompany) {
      const company = clients.find((c) => c.company_name === selectedCompany);
      if (company) {
        const moduleIds = parseModuleIds(company.modules);
        const relatedModules = modules.filter((m) =>
          moduleIds.includes(m.module_id)
        );
        if (company.product_id) {
          setSelectedProduct(company.product_id.toString());
        } else if (company.product?.product_id) {
          setSelectedProduct(company.product.product_id.toString());
        } else {
          setSelectedProduct("");
          setFilteredModules([]);
          toast.error("No product found for this company.");
          return;
        }
        setFilteredModules(relatedModules);
      } else {
        setSelectedProduct("");
        setFilteredModules([]);
        toast.error("Selected company not found.");
      }
    } else {
      setFilteredModules([]);
    }
  }, [companyType, selectedCompany, clients, modules]);

  useEffect(() => {
    if (companyType === "new" && selectedProduct) {
      const related = modules.filter(
        (m) => m.product_id.toString() === selectedProduct
      );
      setFilteredModules(related);
    } else if (companyType === "new") {
      setFilteredModules([]);
    }
  }, [companyType, selectedProduct, modules]);

  const handleCompanyTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = event.target.value;
    setCompanyType(value);
    setSelectedCompany("");
    setCompanyLicense("");
    setEmail("");
    setSelectedProduct("");
    setSelectedModule("");
    setRequirementType("");
    setRequirements("");
    setAttachment(null);
    setFilteredModules([]);
    setErrors({});
    setAttachments([]);
    setCategory(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleProductChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const productId = event.target.value;
    setSelectedProduct(productId);
    if (companyType === "new" && productId) {
      const related = modules.filter(
        (m) => m.product_id.toString() === productId
      );
      setFilteredModules(related);
    } else {
      setFilteredModules([]);
    }
  };

  const handleModuleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModule(event.target.value);
  };

  const handleCancel = () => {
    setCompanyType("existing");
    setSelectedCompany("");
    setCompanyLicense("");
    setEmail("");
    setSelectedProduct("");
    setSelectedModule("");
    setRequirementType("");
    setRequirements("");
    setAttachment(null);
    setFilteredModules([]);
    setCategory(null);
    setErrors({});
    setAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const MAX_ATTACHMENT_SIZE_MB = 5;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate) {
      toast.warning("You don't have permission to Create");
      return;
    }

    const newErrors: { [key: string]: string } = {};
    if (!selectedCompany) newErrors.selectedCompany = "Company is required.";
    if (companyType === "existing") {
      if (!companyLicense) newErrors.companyLicense = "License is required.";
    }
    if (!selectedProduct) newErrors.selectedProduct = "Product is required.";
    if (!selectedModule) newErrors.selectedModule = "Module is required.";
    if (!requirementType) newErrors.requirementType = "Type is required.";
    if (!requirements || requirements.trim().length === 0) {
      newErrors.requirements = "Requirements are required.";
    } else if (requirements.trim().length > 1000) {
      newErrors.requirements = "Requirements cannot exceed 1000 characters.";
    }
    if (attachments?.length > 0) {
      const overSize = attachments.some(
        (file) => file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024
      );
      if (overSize) newErrors.attachments = "Each file must be less than 5MB.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.warning("Please fill all required fields before submitting.");
      return;
    }

    setErrors({});
    setUploadProgress(0);
    try {
      await submitNewEnquiry(
        {
          company_name: selectedCompany,
          company_license:
            companyType === "existing" ? companyLicense : companyLicense,
          email: companyType === "existing" ? email : email,
          product_id: selectedProduct,
          module_id: selectedModule,
          enquiry_type: requirementType,
          requirement: requirements,
          status: "Pending",
          taken_by: EmpId,
          category: category, // Now sending cate_id (number)
        },
        attachments || [],
        (progress) => setUploadProgress(progress),
        empToken
      );

      handleCancel();
      setUploadProgress(0);
      loadEnquiries();
      setAttachments([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setShowNewEnquiry(true); // Close modal on successful save
    } catch (err) {
      console.error("Enquiry submission failed:", err);
    }
  };

  const loadEnquiries = async () => {
    if (!canRead) {
      setDataSource([]);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchEnquiriesForTable(empToken);
      setDataSource(data);
    } catch (error) {
      console.error("Failed to fetch enquiries:", error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEnquiries();
    const intervalId = setInterval(() => {
      loadEnquiries();
    }, 2000);
    return () => clearInterval(intervalId);
  }, [canRead, empToken]);

  const filteredData = useMemo(() => {
    let data = dataSource;

    // Global search
    if (searchText) {
      const lowercased = searchText.toLowerCase();
      data = data.filter((row) => {
        const categoryName = categories.find((c) => c.cate_id === row.category)?.cate_name || "";
        const fieldsToSearch = [
          row.enquiry_id,
          row.company_name,
          row.company_license,
          row.email,
          row.enquiry_type,
          row.requirement,
          row.status,
          categoryName,
          row.note,
          row.rejection_reason,
          row.taken_by,
          row.date,
        ];
        return fieldsToSearch.some((field) =>
          (field || "").toLowerCase().includes(lowercased)
        );
      });
    }

    // Specific filters
    if (filters.enquiryId.length > 0) {
      data = data.filter(row => filters.enquiryId.includes(row.enquiry_id));
    }
    if (filters.companyName.length > 0) {
      data = data.filter(row => filters.companyName.includes(row.company_name));
    }
    if (filters.reqType.length > 0) {
      data = data.filter(row => filters.reqType.includes(row.enquiry_type));
    }
    if (filters.category.length > 0) {
      data = data.filter(row => {
        const rowCategoryName = categories.find((c) => c.cate_id === row.category)?.cate_name || "";
        return filters.category.includes(rowCategoryName);
      });
    }

    return data;
  }, [searchText, dataSource, categories, filters]);


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const overSize = files.some(
      (file) => file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024
    );
    if (overSize) {
      setErrors((prev) => ({
        ...prev,
        attachment: `Each file must be less than ${MAX_ATTACHMENT_SIZE_MB}MB.`,
      }));
      setAttachments([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.attachment;
        return newErrors;
      });
      setAttachments(files);
    }
  };

  // Edit Modal Handlers
  const handleEditClick = (record: TableEnquiryRow) => {
    if (!canUpdate) {
      toast.warning("You don't have permission to Edit");
      return;
    }
    setEditEnquiry(record);
    setEditRequirementType(record.enquiry_type || "");
    setEditRequirements(record.requirement || "");
    setEditAttachments([]); // Reset attachments; existing files are not fetched
    setEditEnquiryCategory(record.category || null); // Now using cate_id
    setEditModalVisible(true);
    setErrors({});
  };

  const handleEditSubmit = async () => {
    if (!canUpdate) {
      toast.warning("You don't have permission to Edit");
      return;
    }
    if (!editEnquiry) return;

    const newErrors: { [key: string]: string } = {};
    if (!editRequirementType) {
      newErrors.requirementType = "Requirement Type is required.";
    }
    if (!editRequirements || editRequirements.trim().length === 0) {
      newErrors.requirements = "Requirements are required.";
    } else if (editRequirements.trim().length > 1000) {
      newErrors.requirements = "Requirements cannot exceed 1000 characters.";
    }
    if (editAttachments?.length > 0) {
      const overSize = editAttachments.some(
        (file) => file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024
      );
      if (overSize) newErrors.attachments = "Each file must be less than 5MB.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.warning("Please fill all required fields before submitting.");
      return;
    }

    try {
      await updateEnquiry(
        editEnquiry.enquiry_id,
        {
          requirement: editRequirements,
          enquiry_type: editRequirementType,
          category: editEnquiryCategory, // Now sending cate_id
        },
        editAttachments,
        empToken
      );
      setEditModalVisible(false); // Close modal on successful edit
      setEditEnquiry(null);
      setEditRequirementType("");
      setEditRequirements("");
      setEditAttachments([]);
      setEditEnquiryCategory(null);
      if (editFileInputRef.current) {
        editFileInputRef.current.value = "";
      }
      loadEnquiries();
    } catch (err) {
      console.error("Enquiry update failed:", err);
    }
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditEnquiry(null);
    setEditRequirementType("");
    setEditRequirements("");
    setEditAttachments([]);
    setErrors({});
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
  };

  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const overSize = files.some(
      (file) => file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024
    );
    if (overSize) {
      setErrors((prev) => ({
        ...prev,
        attachment: `Each file must be less than ${MAX_ATTACHMENT_SIZE_MB}MB.`,
      }));
      setEditAttachments([]);
      if (editFileInputRef.current) {
        editFileInputRef.current.value = "";
      }
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.attachment;
        return newErrors;
      });
      setEditAttachments(files);
    }
  };

  // Delete Modal Handlers
  const handleDeleteClick = (enquiryId: string) => {
    if (!canDelete) {
      toast.warning("You don't have permission to Delete");
      return;
    }
    setDeleteEnquiryId(enquiryId);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete) {
      toast.warning("You don't have permission");
      return;
    }
    if (!deleteEnquiryId) return;
    try {
      await deleteEnquiry(deleteEnquiryId, empToken);
      setDeleteModalVisible(false);
      setDeleteEnquiryId(null);
      loadEnquiries();
    } catch (err) {
      console.error("Enquiry deletion failed:", err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeleteEnquiryId(null);
  };

  const handleEditReset = () => {
    setEditRequirementType("");
    setEditRequirements("");
    setEditAttachments([]);
    setEditEnquiryCategory(null);
    setErrors({});
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
  };

  const columns = [
    {
      title: "Enquiry ID",
      dataIndex: "enquiry_id",
      fixed: "left",
      width: 120,
      sorter: (a: TableEnquiryRow, b: TableEnquiryRow) =>
        a.enquiry_id.localeCompare(b.enquiry_id),
    },
    {
      title: "Company License",
      dataIndex: "company_license",
      width: 180,
      fixed: "left",
      sorter: (a: TableEnquiryRow, b: TableEnquiryRow) =>
        (a.company_license || "").localeCompare(b.company_license || ""),
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
      width: 430,
      sorter: (a: TableEnquiryRow, b: TableEnquiryRow) =>
        a.company_name.localeCompare(b.company_name),
      render: (text: string) => text.toUpperCase(),
    },
    {
      title: "Date of Enquiry",
      dataIndex: "date",
      width: 170,
      sorter: (a: TableEnquiryRow, b: TableEnquiryRow) =>
        new Date(a.date).getTime() - new Date(b.date).getTime(),
      render: (date: string) =>
        new Date(date)
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          .replace(/ /g, "-"),
    },
    {
      title: "Req Type",
      dataIndex: "enquiry_type",
      width: 120,
      sorter: (a: TableEnquiryRow, b: TableEnquiryRow) =>
        a.enquiry_type?.localeCompare(b.enquiry_type || ""),
      render: (text: string) => (
        <span
          className="text-white rounded px-2 py-1"
          style={{
            backgroundColor: text === "Update" ? "#1763ad" : "red",
            fontSize:
              window.innerWidth < 576
                ? "10px"
                : window.innerWidth < 768
                ? "11px"
                : "12px",
          }}
        >
          {text || "-"}
        </span>
      ),
    },
    {
      title: "Requirement",
      dataIndex: "requirement",
      width: 200,
      sorter: (a: TableEnquiryRow, b: TableEnquiryRow) =>
        (a.requirement || "").localeCompare(b.requirement || ""),
      render: (details: string) => {
        const maxLength = 20;
        const displayText =
          details && details.length > maxLength
            ? `${details.slice(0, maxLength)}...`
            : details;
        return (
          <div
            style={{
              fontSize:
                window.innerWidth < 576
                  ? "10px"
                  : window.innerWidth < 768
                  ? "11px"
                  : "12px",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              whiteSpace: "normal",
              display: "block",
              maxWidth: "100%",
              maxHeight: "50px",
              overflowY: "auto",
            }}
          >
            {displayText || "-"}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 170,
      sorter: (a: TableEnquiryRow, b: TableEnquiryRow) =>
        (a.status || "").localeCompare(b.status || ""),
      render: (status: string) => {
        const statusObj =
          status in statusColors
            ? statusColors[status as keyof typeof statusColors]
            : { color: "#0d6efd", icon: "✔️" };
        return (
          <Tag
            bordered
            style={{
              border: `1px solid ${statusObj.color}`,
              color: statusObj.color,
              backgroundColor: "transparent",
              textTransform: "capitalize",
              fontSize:
                window.innerWidth < 576
                  ? "10px"
                  : window.innerWidth < 768
                  ? "11px"
                  : "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "16px",
              padding: "2px 10px",
            }}
          >
            <span>{statusObj.icon}</span>
            <span>{status || "Pending"}</span>
          </Tag>
        );
      },
    },
    {
      title: "Work Category",
      dataIndex: "category",
      width: 200,
      sorter: (a: TableEnquiryRow, b: TableEnquiryRow) => {
        const aName =
          categories.find((c) => c.cate_id === a.category)?.cate_name || "";
        const bName =
          categories.find((c) => c.cate_id === b.category)?.cate_name || "";
        return aName.localeCompare(bName);
      },
      render: (category: number | null) => {
        const categoryName =
          categories.find((c) => c.cate_id === category)?.cate_name || "N/A";
        return (
          <span style={{ textTransform: "capitalize" }}>{categoryName}</span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: 200,
      fixed: "right",
      render: (_: any, record: TableEnquiryRow) => (
        <div className="action-icon d-inline-flex">
          <Popover
            content="Enquiry Details"
            trigger="hover"
            overlayInnerStyle={{
              backgroundColor: "#b7a4dcff",
              color: "white",
              border: "none",
              borderRadius: "4px",
            }}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRecord(record);
                setIsModalVisible(true);
              }}
              style={{
                marginLeft: 8,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "#e6f4ff",
                cursor: "pointer",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#bae7ff")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#e6f4ff")
              }
            >
              <EyeOutlined style={{ fontSize: 14, color: "#1890ff" }} />
            </span>
          </Popover>
          <span
            className="ms-2"
            onClick={() => handleEditClick(record)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#e6f4ff",
              cursor: "pointer",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#bae7ff")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#e6f4ff")
            }
          >
            <i
              className="ti ti-edit"
              style={{ fontSize: 14, color: "#1890ff" }}
            />
          </span>
          <span
            className="ms-2"
            onClick={() => handleDeleteClick(record.enquiry_id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#fff1f0",
              cursor: "pointer",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#ffccc7")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#fff1f0")
            }
          >
            <i
              className="ti ti-trash"
              style={{ fontSize: 14, color: "#ff4d4f" }}
            />
          </span>
        </div>
      ),
    },
  ];

  const handleEnquirySelectionChange = (
    selectedRowKeys: any[],
    selectedRows: any[]
  ) => {
    const selectedEnquiryIDs = selectedRows.map((row) => row.enquiry_id);
    setSelectedEnquiryIds(selectedEnquiryIDs);
  };

  // Category Handlers
  const handleAddCategory = async () => {
    if (!canCreate) {
      toast.warning("You don't have permission to Create");
      return;
    }
    if (!newCategoryName.trim()) {
      toast.warning("Please fill the required field (Category Name).");
      return;
    }
    const payload: CategoryPayload = { cate_name: newCategoryName };
    const success = await createCategory(payload, empToken);
    if (success) {
      setNewCategoryName("");
      setAddCategoryModalVisible(false);
      const updatedCategories = await getAllCategories(empToken);
      setCategories(updatedCategories);
    }
  };

  const handleEditCategory = async () => {
    if (!canUpdate || !selectedCategory) {
      toast.warning(
        "You don't have permission to Edit or no category selected"
      );
      return;
    }
    const payload: CategoryPayload = { cate_name: selectedCategory.cate_name };
    const success = await updateCategory(
      selectedCategory.cate_id,
      payload,
      empToken
    );
    if (success) {
      setSelectedCategory(null);
      setEditCategoryModalVisible(false);
      setManageCategoryModalVisible(false); // Close Manage Categories modal when Edit Category is saved
      const updatedCategories = await getAllCategories(empToken);
      setCategories(updatedCategories);
    }
  };

  const handleViewCategory = async (cateId: number) => {
    if (!canRead) {
      toast.warning("You don't have permission to View");
      return;
    }
    const category = await getCategoryById(cateId, empToken);
    if (category) {
      setSelectedCategory(category);
      setViewCategoryModalVisible(true);
    }
  };

  const categoryColumns = [
    {
      title: "S.No",
      key: "serial",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "cate_name",
      key: "cate_name",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: CategoryResponse) => (
        <div>
          <EditOutlined
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedCategory(record);
              setEditCategoryModalVisible(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Enquiries</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Enquiries
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="export-selected-excel"
                    icon={<FileExcelOutlined />}
                    onClick={() =>
                      exportSelectedEnquiriesExcel(selectedEnquiryIds, empToken)
                    }
                  >
                    Export Selected as Excel
                  </Menu.Item>
                  <Menu.Item
                    key="export-selected-pdf"
                    icon={<FilePdfOutlined />}
                    onClick={() =>
                      exportSelectedEnquiriesPdf(selectedEnquiryIds, empToken)
                    }
                  >
                    Export Selected as PDF
                  </Menu.Item>
                  <Menu.Item
                    key="export-all-excel"
                    icon={<FileExcelOutlined />}
                    onClick={() => exportAllEnquiriesExcel(empToken)}
                  >
                    Export All as Excel
                  </Menu.Item>
                  <Menu.Item
                    key="export-all-pdf"
                    icon={<FilePdfOutlined />}
                    onClick={() => exportAllEnquiriesPdf(empToken)}
                  >
                    Export All as PDF
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
            >
              <Button
                type="primary"
                icon={<ExportOutlined />}
                className="btn btn-primary d-flex justify-content-center align-items-center p-2 rounded"
              />
            </Dropdown>
            <Button
              className={`px-4 py-2 rounded ${
                activeEnquiryTab === "new"
                  ? "btn-light text-primary border border-primary"
                  : "btn-primary text-white"
              }`}
              icon={<PlusCircleOutlined />}
              onClick={() => handleNewEnquiryClick("new")}
            >
              <span className="w-full text-center block">New Enquiry</span>
            </Button>
            <Button
              className={`px-4 py-2 rounded ${
                activeEnquiryTab === "status"
                  ? "btn-light text-primary border border-primary"
                  : "btn-primary text-white"
              }`}
              onClick={() => handleEnquiryStatusClick("status")}
            >
              <span className="w-full text-center block">Enquiry Status</span>
            </Button>
            <CollapseHeader />
          </div>
        </div>
        {showNewEnquiry && (
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Enquiry Form</h5>
              <div>
                <Button
                  className="btn-light text-primary border border-primary me-2"
                  onClick={() => setAddCategoryModalVisible(true)}
                >
                  Add Category
                </Button>
                <Button
                  className="btn-light text-primary border border-primary"
                  onClick={() => setManageCategoryModalVisible(true)}
                >
                  Manage Categories
                </Button>
              </div>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">
                    Company Selection <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-10">
                    <select
                      className="form-select"
                      value={companyType}
                      onChange={handleCompanyTypeChange}
                    >
                      <option value="existing">Existing</option>
                      <option value="new">New</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">
                    Company Licence <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-10">
                    {companyType === "existing" ? (
                      <Select
                        showSearch
                        placeholder="Select License"
                        style={{ width: "100%" }}
                        value={companyLicense || undefined}
                        onChange={(value) => {
                          setCompanyLicense(value);
                          const matched = clients.find(
                            (c) =>
                              c.company_license.toLowerCase() ===
                              value.toLowerCase()
                          );
                          if (matched) {
                            setSelectedCompany(matched.company_name);
                            setEmail(matched.email);
                          }
                        }}
                        filterOption={(input, option) =>
                          (option?.label as string)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        options={licenseList.map((license) => ({
                          value: license,
                          label: license,
                        }))}
                      />
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter company license"
                        value={companyLicense}
                        onChange={(e) => setCompanyLicense(e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">
                    {companyType === "existing"
                      ? "Existing Company"
                      : "Company Name"}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-10">
                    {companyType === "existing" ? (
                      <Select
                        showSearch
                        placeholder="Select Company"
                        style={{ width: "100%" }}
                        value={selectedCompany || undefined}
                        onChange={(value) => {
                          setSelectedCompany(value);
                          const matched = clients.find(
                            (c) =>
                              c.company_name?.toLowerCase() ===
                              value?.toLowerCase()
                          );
                          if (matched) {
                            setCompanyLicense(matched.company_license || "");
                            setEmail(matched.email || "");
                            if (matched.product_id) {
                              setSelectedProduct(matched.product_id.toString());
                            } else if (matched.product?.product_id) {
                              setSelectedProduct(
                                matched.product.product_id.toString()
                              );
                            } else {
                              setSelectedProduct("");
                            }
                            if (
                              matched.modules &&
                              Array.isArray(matched.modules)
                            ) {
                              const moduleIds = matched.modules
                                .flatMap((m) =>
                                  m.split(",").map((id) => parseInt(id.trim()))
                                )
                                .filter((id) => !isNaN(id));
                              const matchedModules = modules.filter((m) =>
                                moduleIds.includes(m.module_id)
                              );
                              setFilteredModules(matchedModules);
                            } else {
                              setFilteredModules([]);
                            }
                          } else {
                            setCompanyLicense("");
                            setEmail("");
                            setSelectedProduct("");
                            setFilteredModules([]);
                          }
                        }}
                        filterOption={(input, option) =>
                          (option?.value as string)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        options={clients.map((c) => ({
                          value: c.company_name,
                          label: c.company_name,
                        }))}
                      />
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter company name"
                        value={selectedCompany}
                        onChange={(e) => setSelectedCompany(e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">
                    Product <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-10">
                    <select
                      className="form-select"
                      value={selectedProduct}
                      onChange={handleProductChange}
                      disabled={companyType === "existing"}
                    >
                      <option value="">-- Select Product --</option>
                      {companyType === "new"
                        ? products.map((product) => (
                            <option
                              key={product.product_id}
                              value={product.product_id.toString()}
                            >
                              {product.product_name}
                            </option>
                          ))
                        : Array.from(
                            new Set(filteredModules.map((m) => m.product_id))
                          ).map((productId) => {
                            const product = products.find(
                              (p) => p.product_id === productId
                            );
                            return product ? (
                              <option
                                key={product.product_id}
                                value={product.product_id.toString()}
                              >
                                {product.product_name}
                              </option>
                            ) : null;
                          })}
                    </select>
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">
                    Module <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-10">
                    <select
                      className="form-select"
                      value={selectedModule}
                      onChange={handleModuleChange}
                    >
                      <option value="">-- Select Module --</option>
                      {filteredModules.map((module) => (
                        <option key={module.module_id} value={module.module_id}>
                          {module.module_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">
                    Requirement Type <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-10">
                    <select
                      className="form-select"
                      value={requirementType}
                      onChange={(e) => setRequirementType(e.target.value)}
                    >
                      <option>-- Select --</option>
                      <option value="Update">Update</option>
                      <option value="Issues">Issue</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">Email</label>
                  <div className="col-md-10">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">
                    Work Category
                  </label>
                  <div className="col-md-10">
                    <select
                      className="form-select"
                      value={category || ""}
                      onChange={(e) =>
                        setCategory(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    >
                      <option value="">-- Select Category --</option>
                      {categories.map((cat) => (
                        <option key={cat.cate_id} value={cat.cate_id}>
                          {cat.cate_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">
                    Requirements <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-10">
                    <textarea
                      rows={5}
                      className="form-control"
                      placeholder="Enter requirements here"
                      value={requirements}
                      onChange={(e) => setRequirements(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mb-3 row">
                  <label className="col-form-label col-md-2">Attachment</label>
                  <div className="col-md-10">
                    <input
                      type="file"
                      className="form-control"
                      onChange={handleFileChange}
                      multiple
                      ref={fileInputRef}
                    />
                    {errors.attachment && (
                      <div style={{ color: "red" }}>{errors.attachment}</div>
                    )}
                  </div>
                </div>
                {uploadProgress !== null && (
                  <div className="progress my-2">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      {`${uploadProgress}%`}
                    </div>
                  </div>
                )}
                <div className="mb-3 row">
                  <div className="col-md-10 offset-md-2">
                    <button type="submit" className="btn btn-primary">
                      Submit
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary ms-2"
                      onClick={handleCancel}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
        {showEnquiryStatus && (
          <div className="row">
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h5>Status Details</h5>
                <div className="d-flex right-content align-items-center gap-3">
                  <button
                    className="btn btn-outline-primary d-flex justify-content-center align-items-center position-relative"
                    onClick={showModal}
                    aria-label={`Open filters ${
                      activeFiltersCount > 0
                        ? `(${activeFiltersCount} active)`
                        : ""
                    }`}
                    type="button"
                  >
                    <i className="ti ti-filter me-1" aria-hidden="true"></i>
                    Filters
                    {activeFiltersCount > 0 && (
                      <span
                        className="badge bg-primary text-white position-absolute top-0 start-100 translate-middle ms-1 rounded-circle"
                        style={{ fontSize: "0.75em" }}
                      >
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                  <Input.Search
                    placeholder="Search enquiries..."
                    allowClear
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ maxWidth: 300 }}
                  />
                </div>
              </div>

              <div className="card-body p-0">
                <Table
                  dataSource={filteredData}
                  columns={columns}
                  Selection={true}
                  rowKey={"enquiry_id"}
                  onSelectionChange={handleEnquirySelectionChange}
                  scroll={{ x: 1500 }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Enquiry Details Modal */}
      {isModalVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setIsModalVisible(false)}
        >
          <div
            style={{
              width: 600,
              backgroundColor: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: "#1e3a8a",
                color: "white",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: 56,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: "white",
                }}
              >
                Enquiry Details
              </h3>
              <button
                onClick={() => setIsModalVisible(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  fontSize: 18,
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background =
                    "rgba(255, 255, 255, 0.2)";
                  (e.target as HTMLElement).style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background =
                    "rgba(255, 255, 255, 0.1)";
                  (e.target as HTMLElement).style.transform = "scale(1)";
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
              {selectedRecord && (
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <strong>ID:</strong> {selectedRecord.enquiry_id}
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Status:</strong>{" "}
                    <Tag
                      bordered
                      style={{
                        border: `1px solid ${
                          selectedRecord.status &&
                          selectedRecord.status in statusColors
                            ? statusColors[
                                selectedRecord.status as keyof typeof statusColors
                              ].color
                            : "#0d6efd"
                        }`,
                        color:
                          selectedRecord.status &&
                          selectedRecord.status in statusColors
                            ? statusColors[
                                selectedRecord.status as keyof typeof statusColors
                              ].color
                            : "#0d6efd",
                        backgroundColor: "transparent",
                        textTransform: "capitalize",
                        fontSize:
                          window.innerWidth < 576
                            ? "10px"
                            : window.innerWidth < 768
                            ? "11px"
                            : "12px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        borderRadius: "15px",
                      }}
                    >
                      <span>
                        {selectedRecord.status &&
                        selectedRecord.status in statusColors
                          ? statusColors[
                              selectedRecord.status as keyof typeof statusColors
                            ].icon
                          : "✔️"}
                      </span>
                      <span>{selectedRecord.status || "Pending"}</span>
                    </Tag>
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Company:</strong>{" "}
                    {selectedRecord.company_name || "-"}
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>License:</strong>{" "}
                    {selectedRecord.company_license || "-"}
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Email:</strong> {selectedRecord.email || "-"}
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Category:</strong>{" "}
                    {categories.find(
                      (c) => c.cate_id === selectedRecord.category
                    )?.cate_name || "-"}
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Product:</strong>{" "}
                    {products.find(
                      (p) => p.product_id === selectedRecord.product_id
                    )?.product_name || "-"}
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Module:</strong>{" "}
                    {modules.find(
                      (m) => m.module_id === selectedRecord.module_id
                    )?.module_name || "-"}
                  </div>
                  <div className="col-md-12 mt-3">
                    <strong>Requirement:</strong>
                    <div
                      style={{
                        border: "1px solid #dee2e6",
                        padding: "10px",
                        borderRadius: "5px",
                        marginTop: "5px",
                        maxHeight: "150px",
                        overflowY: "auto",
                        backgroundColor: "#f8f9fa",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {selectedRecord.requirement}
                    </div>
                  </div>
                  {["Rejected", "On Hold"].includes(
                    selectedRecord.status || ""
                  ) &&
                    selectedRecord.rejection_reason && (
                      <div className="col-md-12 mt-3">
                        <strong>
                          {selectedRecord.status === "On Hold"
                            ? "Reason for On Hold"
                            : "Rejection Reason"}
                          :
                        </strong>
                        <div
                          style={{
                            border: "1px solid #dee2e6",
                            padding: "10px",
                            borderRadius: "5px",
                            marginTop: "5px",
                            maxHeight: "100px",
                            overflowY: "auto",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {selectedRecord.rejection_reason}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      <div
        className={`modal fade ${filterModalVisible ? "show d-block" : ""}`}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="filterModalLabel"
        aria-hidden={!filterModalVisible}
        key={filterModalVisible ? "filter-open" : "filter-closed"}
        style={{
          display: filterModalVisible ? "block" : "none",
          backgroundColor: filterModalVisible
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
        onClick={handleFilterCancel}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          role="document"
          style={{ maxWidth: "60vw", width: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content" ref={filterModalRef}>
            <div className="modal-header">
              <h5 className="modal-title" id="filterModalLabel">
                Apply Filters
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleFilterCancel}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-0">
              <div className="row g-3 px-3 py-3">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Enquiry ID</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Select Enquiry ID"
                    value={modalFilters.enquiryId}
                    onChange={(value) =>
                      setModalFilters((prev) => ({ ...prev, enquiryId: value }))
                    }
                    options={uniqueEnquiryIds.map((id) => ({
                      value: id,
                      label: id,
                    }))}
                    allowClear
                    showSearch
                    maxTagCount={2}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    getPopupContainer={() => filterModalRef.current!}
                    dropdownRender={(menus) =>
                      dropdownRender(
                        menus,
                        handleSelectAllEnquiryId,
                        enquiryIdChecked,
                        enquiryIdIndeterminate,
                        "Enquiry IDs"
                      )
                    }
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Company Name</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Select Company Name"
                    value={modalFilters.companyName}
                    onChange={(value) =>
                      setModalFilters((prev) => ({
                        ...prev,
                        companyName: value,
                      }))
                    }
                    options={uniqueCompanyNames.map((name) => ({
                      value: name,
                      label: name,
                    }))}
                    allowClear
                    showSearch
                    maxTagCount={2}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    getPopupContainer={() => filterModalRef.current!}
                    dropdownRender={(menus) =>
                      dropdownRender(
                        menus,
                        handleSelectAllCompanyName,
                        companyNameChecked,
                        companyNameIndeterminate,
                        "Company Names"
                      )
                    }
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Req Type</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Select Req Type"
                    value={modalFilters.reqType}
                    onChange={(value) =>
                      setModalFilters((prev) => ({ ...prev, reqType: value }))
                    }
                    options={uniqueReqTypes.map((type) => ({
                      value: type,
                      label: capitalizeFirstLetter(type),
                    }))}
                    allowClear
                    showSearch
                    maxTagCount={2}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    getPopupContainer={() => filterModalRef.current!}
                    dropdownRender={(menus) =>
                      dropdownRender(
                        menus,
                        handleSelectAllReqType,
                        reqTypeChecked,
                        reqTypeIndeterminate,
                        "Req Types"
                      )
                    }
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Work Category</label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Select Work Category"
                    value={modalFilters.category}
                    onChange={(value) =>
                      setModalFilters((prev) => ({ ...prev, category: value }))
                    }
                    options={categories.map((cat) => ({
                      value: cat.cate_name,
                      label: cat.cate_name,
                    }))}
                    allowClear
                    showSearch
                    maxTagCount={2}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    getPopupContainer={() => filterModalRef.current!}
                    dropdownRender={(menus) =>
                      dropdownRender(
                        menus,
                        handleSelectAllCategory,
                        categoryChecked,
                        categoryIndeterminate,
                        "Work Categories"
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-white me-2"
                onClick={handleFilterClear}
              >
                Clear All
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFilterApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
      {filterModalVisible && (
        <div
          className="modal-backdrop fade show"
          onClick={handleFilterCancel}
        ></div>
      )}

      <Drawer
        title="Edit Enquiry"
        placement="right"
        open={editModalVisible}
        onClose={handleEditCancel}
        width={600}
        styles={{
          header: {
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "#fff",
          },
          body: {
            paddingBottom: 80,
            maxHeight: "calc(100vh - 110px)",
            overflowY: "auto",
          },
        }}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button style={{ marginRight: 8 }} onClick={handleEditReset}>
              Reset
            </Button>
            <Button type="primary" onClick={handleEditSubmit}>
              Save
            </Button>
          </div>
        }
      >
        {editEnquiry && (
          <form className="container-fluid">
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label">
                Company Licence <span className="text-danger">*</span>
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  className="form-control"
                  value={editEnquiry.company_license || ""}
                  disabled
                />
              </div>
            </div>
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label">
                Company Name <span className="text-danger">*</span>
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  className="form-control"
                  value={editEnquiry.company_name || ""}
                  disabled
                />
              </div>
            </div>
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label">
                Product <span className="text-danger">*</span>
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  className="form-control"
                  value={
                    products.find(
                      (p) => p.product_id === editEnquiry.product_id
                    )?.product_name || "-"
                  }
                  disabled
                />
              </div>
            </div>
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label">
                Module <span className="text-danger">*</span>
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  className="form-control"
                  value={
                    modules.find((m) => m.module_id === editEnquiry.module_id)
                      ?.module_name || "-"
                  }
                  disabled
                />
              </div>
            </div>
            {editEnquiry.email && (
              <div className="row mb-3">
                <label className="col-sm-4 col-form-label">Email</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    className="form-control"
                    value={editEnquiry.email || "No Email"}
                    disabled
                  />
                </div>
              </div>
            )}
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label">Work Category</label>
              <div className="col-sm-8">
                <select
                  className="form-select"
                  value={editEnquiryCategory || ""}
                  onChange={(e) =>
                    setEditEnquiryCategory(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((cat) => (
                    <option key={cat.cate_id} value={cat.cate_id}>
                      {cat.cate_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label">
                Requirement Type <span className="text-danger">*</span>
              </label>
              <div className="col-sm-8">
                <select
                  className="form-select"
                  value={editRequirementType}
                  onChange={(e) => setEditRequirementType(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  <option value="Update">Update</option>
                  <option value="Issues">Issue</option>
                </select>
                {errors.requirementType && (
                  <div className="text-danger">{errors.requirementType}</div>
                )}
              </div>
            </div>
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label">
                Requirements <span className="text-danger">*</span>
              </label>
              <div className="col-sm-8">
                <textarea
                  rows={5}
                  className="form-control"
                  value={editRequirements}
                  onChange={(e) => setEditRequirements(e.target.value)}
                />
                {errors.requirements && (
                  <div className="text-danger">{errors.requirements}</div>
                )}
              </div>
            </div>
          </form>
        )}
      </Drawer>
      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this enquiry?</p>
      </Modal>
      {/* Add Category Modal */}
      <Modal
        title="Add Category"
        open={addCategoryModalVisible}
        onOk={handleAddCategory}
        onCancel={() => {
          setAddCategoryModalVisible(false);
          setNewCategoryName("");
        }}
        okText="Save"
        cancelText="Cancel"
        zIndex={9999}
        maskStyle={{
          backgroundColor: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
        }}
      >
        <div style={{ padding: "24px" }}>
          <div className="mb-3 row">
            <label className="col-sm-4 col-form-label">
              Category Name <span className="text-danger">*</span>
            </label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Enter category name"
              />
            </div>
          </div>
        </div>
      </Modal>
      {/* Edit Category Modal */}
      <Modal
        title="Edit Category"
        open={editCategoryModalVisible}
        onOk={handleEditCategory}
        onCancel={() => {
          setEditCategoryModalVisible(false);
          setManageCategoryModalVisible(false); // Close Manage Categories modal when Edit Category is closed
          setSelectedCategory(null);
        }}
        okText="Save"
        cancelText="Cancel"
        zIndex={9999}
        maskStyle={{
          backgroundColor: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
        }}
      >
        <div style={{ padding: "24px" }}>
          <div className="mb-3 row">
            <label className="col-sm-4 col-form-label">
              Category Name <span className="text-danger">*</span>
            </label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                value={selectedCategory?.cate_name || ""}
                onChange={(e) => {
                  if (selectedCategory) {
                    setSelectedCategory({
                      ...selectedCategory,
                      cate_name: e.target.value,
                    });
                  }
                }}
                placeholder="Enter category name"
              />
            </div>
          </div>
        </div>
      </Modal>
      {/* View Category Modal */}
      <Modal
        title="View Category"
        open={viewCategoryModalVisible}
        onOk={() => setViewCategoryModalVisible(false)}
        onCancel={() => setViewCategoryModalVisible(false)}
        okText="Close"
        cancelButtonProps={{ style: { display: "none" } }}
        zIndex={9999}
        maskStyle={{
          backgroundColor: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
        }}
      >
        <div style={{ padding: "24px" }}>
          <div className="mb-3 row">
            <label className="col-sm-4 col-form-label">Category ID:</label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                value={selectedCategory?.cate_id || ""}
                disabled
              />
            </div>
          </div>
          <div className="mb-3 row">
            <label className="col-sm-4 col-form-label">Category Name:</label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                value={selectedCategory?.cate_name || ""}
                disabled
              />
            </div>
          </div>
          <div className="mb-3 row">
            <label className="col-sm-4 col-form-label">Created At:</label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                value={selectedCategory?.created_at || ""}
                disabled
              />
            </div>
          </div>
          <div className="mb-3 row">
            <label className="col-sm-4 col-form-label">Updated At:</label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                value={selectedCategory?.updated_at || ""}
                disabled
              />
            </div>
          </div>
        </div>
      </Modal>
      {/* Manage Categories Modal */}
      <Modal
        title="Manage Categories"
        open={manageCategoryModalVisible}
        onCancel={() => setManageCategoryModalVisible(false)}
        footer={null}
        centered
        width={900}
        zIndex={9999}
        maskStyle={{
          backgroundColor: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
        }}
      >
        <Table
          dataSource={categories}
          columns={categoryColumns}
          rowKey="cate_id"
        />
      </Modal>
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2024 - 2025 © IATT.</p>
        <p>
          Designed & Developed By{" "}
          <Link to="#" className="text-primary">
            IATT
          </Link>
        </p>
      </div>
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{
          zIndex: 10000,
          position: "fixed",
          top: "20px",
          right: "20px",
          width: "300px",
          maxWidth: "300px",
          margin: "0 auto",
        }}
      />
    </div>
  );
};

export default Enquiry;
